import React from 'react';
import { Select, Radio, Alert } from 'antd';
import { CustomOfferFormData } from '../types';
import FormInput from './FormInput';
import { USER_TYPES } from 'src/enums/EUser';

interface BasicInformationFormProps {
	formData: CustomOfferFormData;
	updateFormData: (updates: Partial<CustomOfferFormData>) => void;
	getFieldError: (fieldName: string) => string;
	isClient: boolean;
}

const BasicInformationForm: React.FC<BasicInformationFormProps> = ({
	formData,
	updateFormData,
	getFieldError,
	isClient,
}) => {
	const handleNameChange = (value: any): void => {
		updateFormData({ name: value as string });
	};

	const handleProductNameChange = (value: any): void => {
		updateFormData({ productName: value as string });
	};

	const handleUserTypeChange = (value: string): void => {
		const newUserType = value as USER_TYPES.CLIENT | USER_TYPES.RESELLER;
		updateFormData({
			forUserType: newUserType,
			type: newUserType === USER_TYPES.CLIENT ? 'perSeat' : formData.type,
			quantity: newUserType === USER_TYPES.CLIENT ? 1 : formData.quantity,
		});
	};

	const handleTypeChange = (e: any): void => {
		updateFormData({
			type: e.target.value as 'perSeat' | 'unlimited',
			quantity: e.target.value === 'unlimited' ? 1 : formData.quantity,
		});
	};

	const handleQuantityChange = (value: any): void => {
		updateFormData({ quantity: value as number });
	};

	const handlePriceQuoteChange = (value: any): void => {
		updateFormData({ priceQuote: value as number });
	};

	return (
		<div className="space-y-4">
			<FormInput
				label="Client Name (Optional)"
				value={formData.name}
				onChange={handleNameChange}
				placeholder="Enter client name"
				error={getFieldError('name')}
			/>

			<FormInput
				label="Product Name"
				value={formData.productName}
				onChange={handleProductNameChange}
				placeholder="Custom offer name"
				required
				error={getFieldError('productName')}
			/>

			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">For User Type *</label>
				<Select
					value={formData.forUserType}
					onChange={handleUserTypeChange}
					className="w-full"
					style={{ width: '100%' }}
				>
					<Select.Option value="Client">Client</Select.Option>
					<Select.Option value="Reseller">Reseller</Select.Option>
				</Select>
			</div>

			{!isClient && (
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
					<Radio.Group value={formData.type} onChange={handleTypeChange} className="space-y-2">
						<div className="flex items-center">
							<Radio value="unlimited" className="mr-2">
								<span className="text-sm text-gray-900">Unlimited</span>
							</Radio>
						</div>
						<div className="flex items-center">
							<Radio value="perSeat" className="mr-2">
								<span className="text-sm text-gray-900">Per Seat</span>
							</Radio>
						</div>
					</Radio.Group>
				</div>
			)}

			{!isClient && formData.type === 'perSeat' && (
				<FormInput
					label="Quantity"
					value={formData.quantity}
					onChange={handleQuantityChange}
					type="number"
					required
					error={getFieldError('quantity')}
				/>
			)}

			{isClient && (
				<Alert
					message="Agents"
					description="Fixed at 1 agent for Client offers"
					type="info"
					showIcon
					className="bg-gray-50"
				/>
			)}

			<FormInput
				label="Price Quote (USD)"
				value={formData.paymentMethod === 'wire_transfer' ? 0 : formData.priceQuote}
				onChange={handlePriceQuoteChange}
				type="number"
				required
				disabled={formData.paymentMethod === 'wire_transfer'}
				error={getFieldError('priceQuote')}
			/>
		</div>
	);
};

export default BasicInformationForm;
